import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { useAuth } from '@/contexts/AuthContext';
import { PageContainer, PageHeader } from '@/components/layout';
import { Upload, Loader2, CheckCircle2, Clock, AlertCircle, Trash2, X, Copy, Check, HelpCircle, Info, FileDown, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { API_BASE_URL as BASE } from '@/api/apiConfig';
import { sseService } from '@/services/sseService';
import { AiUsageStatus } from '@/api/dashboardApi';
import { CustomAltEditor } from '@/components/custom-alt/CustomAltEditor';
const API_BASE_URL = `${BASE}/api/crawler`;
const UPLOADS_BASE_URL = `${BASE}/api/crawler/uploads`;

// 날짜 유틸리티
const today = () => new Date().toISOString().slice(0, 10);
const oneMonthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
};

// 이미지 업로드 최대 크기 (환경변수로 관리)
// 운영: .env.production REACT_APP_MAX_UPLOAD_SIZE_MB=10  (S3 Presigned URL 직접 업로드)
// 로컬: .env.development REACT_APP_MAX_UPLOAD_SIZE_MB=20 (Spring Boot 설정값)
const MAX_UPLOAD_SIZE_MB = parseInt(process.env.REACT_APP_MAX_UPLOAD_SIZE_MB || '20', 10);
const MAX_UPLOAD_SIZE_LABEL = `최대 ${MAX_UPLOAD_SIZE_MB}MB`;

// 인증 토큰이 필요한 이미지를 Blob URL로 보여주는 컴포넌트
function AuthenticatedImage({
  src, alt, className, fallbackClassName
}: {
  src: string;
  alt: string;
  className?: string;
  fallbackClassName?: string;
}) {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    if (!src) return;
    // S3, CDN 등 자체 서버가 아닌 외부 URL은 인증 헤더 없이 바로 표시
    const isOwnApiUrl = src.startsWith(BASE + '/');
    if (src.startsWith('http') && !isOwnApiUrl) {
      setBlobUrl(src);
      return;
    }
    let objectUrl: string | null = null;
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    fetch(src, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(res => {
        if (!res.ok) throw new Error('fetch failed');
        return res.blob();
      })
      .then(blob => {
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch(() => setFailed(true));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (failed || (!blobUrl && !src)) {
    return (
      <div className={`${className} bg-gray-100 flex items-center justify-center text-xs text-gray-400`}>
        No Image
      </div>
    );
  }
  if (!blobUrl) {
    return <div className={`${className} bg-gray-100 animate-pulse`} />;
  }
  return <img src={blobUrl} alt={alt} className={className} onError={() => { setBlobUrl(null); setFailed(true); }} />;
}

// S3 Presigned URL 전용 axios 인스턴스
// axios.defaults.headers.common['Authorization']이 전파되지 않도록 별도 인스턴스 사용
const s3Client = axios.create();

// Axios 인스턴스 생성 (인증 토큰 자동 추가)
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터: 토큰 자동 추가
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const activeTeamId = sessionStorage.getItem('activeTeamId');
    if (activeTeamId && config.headers) {
      config.headers['X-Team-Id'] = activeTeamId;
    }
    // FormData 전송 시 Content-Type 헤더 제거 → axios가 자동으로 multipart/form-data; boundary=... 설정
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터: 401 에러 처리 (AuthContext 전역 인터셉터와 중복 방지)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // AuthContext 전역 인터셉터가 이미 처리 - 여기서는 pathname 가드를 통해 중복 이동 방지
      if (window.location.pathname !== '/login') {
        localStorage.removeItem('authToken');
        sessionStorage.removeItem('authToken');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

type AltGenerationType = 'AI' | 'FALLBACK' | 'PENDING' | 'ERROR' | 'SKIPPED_DECORATIVE';

// 우선순위 정책: Custom Alt > Generated Alt
function getDisplayAlt(img: ImageInfo) {
  if (img.customAltText) return { text: img.customAltText, type: 'CUSTOM' as const };
  if (img.generatedAlt) return { text: img.generatedAlt, type: 'GENERATED' as const };
  return { text: null, type: 'NONE' as const };
}

interface ImageInfo {
  id: number;
  url: string;
  imageSrc: string;
  displayImageUrl?: string;
  generatedAlt: string | null;
  altGenerationType: AltGenerationType;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  createdAt: string;
  updatedAt?: string;
  cachecnt?: number;  // 캐시 사용 횟수
  customAltText: string | null;  // 커스텀 ALT 텍스트
}

interface UploadResult {
  id: number;
  url: string;
  imageSrc: string;
  displayImageUrl?: string;
  generatedAlt: string | null;
  altGenerationType: AltGenerationType;
  status: string;
  createdAt: string;
  updatedAt?: string;
  cachecnt?: number;  // 캐시 사용 횟수
}

type SortBy = 'CREATED_AT' | 'UPDATED_AT' | 'STATUS' | 'URL_NORMALIZED' | 'COMPLETION';
type SortDir = 'ASC' | 'DESC';
interface SortMeta { sortBy: SortBy; sortDir: SortDir; isDefault: boolean; }

interface PageResponse {
  content: ImageInfo[];
  totalPages: number;
  totalElements: number;
  number: number;
  sort?: SortMeta;
  stats?: {
    ai: number;
    fallback: number;
    pending: number;
    error: number;
    skippedDecorative: number;
    total: number;
    aiPercentage: number;
  };
}

interface SelectedFileInfo {
  file: File;
  previewUrl: string;
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  progress: number;
  error?: string;
  result?: UploadResult;
}

function sanitizeUploadAltError(
  generatedAlt: string | null,
  altType: AltGenerationType,
  status: string
): string | null {
  if (!generatedAlt) return generatedAlt;
  if (altType !== 'ERROR' && status !== 'FAILED') return generatedAlt;

  if (/이미지 다운로드 실패|원본 사이트|CDN|Referer|만료된 URL|이미지 업로드/.test(generatedAlt)) {
    return '이미지 분석 중 오류가 발생했습니다. 파일 형식과 접근 권한을 확인한 뒤 다시 시도해 주세요.';
  }

  return '이미지 분석 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
}

async function fetchImageForExcel(
  url: string,
  headers: HeadersInit = {}
): Promise<{ buffer: ArrayBuffer; extension: 'jpeg' | 'png' | 'gif' } | null> {
  try {
    const res = await fetch(url, { headers });
    if (!res.ok) return null;
    const blob = await res.blob();
    const type = blob.type.toLowerCase();
    if (type === 'image/png') return { buffer: await blob.arrayBuffer(), extension: 'png' };
    if (type.includes('jpeg') || type.includes('jpg')) return { buffer: await blob.arrayBuffer(), extension: 'jpeg' };
    if (type === 'image/gif') return { buffer: await blob.arrayBuffer(), extension: 'gif' };
    // bmp, webp 등 미지원 포맷 → canvas로 PNG 변환
    return await new Promise<{ buffer: ArrayBuffer; extension: 'png' } | null>((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(blob);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || 100;
        canvas.height = img.naturalHeight || 100;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (pngBlob) => {
            if (!pngBlob) { resolve(null); return; }
            pngBlob.arrayBuffer()
              .then(buf => resolve({ buffer: buf, extension: 'png' }))
              .catch(() => resolve(null));
          },
          'image/png'
        );
      };
      img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(null); };
      img.src = objectUrl;
    });
  } catch {
    return null;
  }
}

// 브라우저/OS에 따라 MIME type이 비어있거나 application/octet-stream으로 오감지되는
// JFIF 등 이미지 파일을 올바른 MIME type으로 정규화한다.
const IMAGE_EXT_MIME: Record<string, string> = {
  jfif: 'image/jpeg', jpe: 'image/jpeg', jpg: 'image/jpeg', jpeg: 'image/jpeg',
  png: 'image/png', gif: 'image/gif', webp: 'image/webp',
  bmp: 'image/bmp', tiff: 'image/tiff', tif: 'image/tiff',
  ico: 'image/x-icon', avif: 'image/avif', svg: 'image/svg+xml',
};

// 브라우저가 image/jfif, image/jpg, image/pjpeg 등 비표준 MIME을 보고할 수 있다.
// 이들은 모두 image/jpeg로 정규화해야 B/E AI 파이프라인이 처리 가능하다.
const JPEG_MIME_ALIASES = new Set([
  'image/jfif', 'image/jpg', 'image/pjpeg', 'image/x-jpeg', 'image/jpe',
]);

function resolveImageMime(file: File): string {
  const raw = file.type;
  // JPEG 계열 비표준 MIME → image/jpeg 정규화 (핵심 수정)
  if (raw && JPEG_MIME_ALIASES.has(raw)) return 'image/jpeg';
  if (raw && raw.startsWith('image/')) return raw;
  // MIME 없거나 application/octet-stream → 확장자로 판단
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  return IMAGE_EXT_MIME[ext] ?? 'image/jpeg';
}

// 업로드용 파일 정규화: JFIF/JPE 등 비표준 JPEG 계열 확장자를 .jpg로 rename + MIME type 보정
// 백엔드 AI 파이프라인이 .jfif/.jpe 확장자를 인식 못하는 경우를 대비
function normalizeUploadFile(file: File): { blob: Blob; name: string } {
  const mime = resolveImageMime(file);
  // JPEG 계열 비표준 확장자 → .jpg 통일
  const name = file.name.replace(/\.(jfif|jpe|jfif|pjpeg|pjp)$/i, '.jpg');
  if (mime === file.type && name === file.name) return { blob: file, name };
  return { blob: new Blob([file], { type: mime }), name };
}

const formatDateTime = (value?: string): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ko-KR');
};

const formatRelativeTime = (value?: string): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const diffSec = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (diffSec < 60) return `${diffSec}초 전`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}시간 전`;
  const diffDay = Math.floor(diffHour / 24);
  return `${diffDay}일 전`;
};

function AlttextImgUpload() {
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();
  const { user } = useAuth();
  const isSuperAdmin = user?.userRole === 'SUPER_ADMIN';
  const [selectedFiles, setSelectedFiles] = useState<SelectedFileInfo[]>([]);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState<ImageInfo[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'COMPLETED'>('ALL');
  const [allStats, setAllStats] = useState<{ ai: number; fallback: number; pending: number; error: number; skippedDecorative: number; total: number; aiPercentage: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [altTypeFilter, setAltTypeFilter] = useState<AltGenerationType | 'ALL'>('ALL');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [copiedHtmlId, setCopiedHtmlId] = useState<number | null>(null);
  const [regeneratingIds, setRegeneratingIds] = useState<Set<number>>(new Set());
  const [showGuide, setShowGuide] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [tick, setTick] = useState(0); // PENDING 타임아웃 감지용 리렌더 트리거
  const [aiUsage, setAiUsage] = useState<AiUsageStatus | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [maxAllowedSizeKb, setMaxAllowedSizeKb] = useState<number>(0);
  const [useContextProfile, setUseContextProfile] = useState(false);
  const [profileActive, setProfileActive] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>('UPDATED_AT'); // 업로드 기본: 수정일 DESC
  const [sortDir, setSortDir] = useState<SortDir>('DESC');
  const [sortIsDefault, setSortIsDefault] = useState(true);
  const [startDate, setStartDate] = useState(oneMonthAgo());
  const [endDate, setEndDate] = useState(today());

  const fetchAiUsage = async () => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.get(`${BASE}/api/dashboard/ai-usage-status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setAiUsage(response.data);
    } catch (err) {
      // 사용량 조회 실패 시 무시
    }
  };

  const fetchProfileStatus = async () => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.get(`${BASE}/api/user/alt-profiles/status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const { hasProfile, useYn } = response.data;
      setProfileActive(hasProfile);
      setUseContextProfile(hasProfile && useYn === 'Y');
    } catch {
      // 프로필 상태 조회 실패 시 무시
    }
  };

  // 복사 함수
  const handleCopyAlt = async (text: string, imageId: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(imageId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      alert('복사하지 못했습니다.');
    }
  };

  const handleCopyHtml = async (img: ImageInfo) => {
    try {
      const displayAlt = getDisplayAlt(img);
      const htmlTag = `<img src="${img.displayImageUrl ?? img.imageSrc}" alt="${displayAlt.text || ''}" />`;
      await navigator.clipboard.writeText(htmlTag);
      setCopiedHtmlId(img.id);
      setTimeout(() => setCopiedHtmlId(null), 2000);
    } catch (err) {
      alert('복사하지 못했습니다.');
    }
  };

  // 커스텀 ALT 저장 핸들러
  const handleCustomAltSaved = (imageId: number, customAltText: string) => {
    setImages(images.map(img => 
      img.id === imageId 
        ? { ...img, customAltText }
        : img
    ));
  };

  // 커스텀 ALT 삭제 핸들러
  const handleCustomAltDeleted = (imageId: number) => {
    setImages(images.map(img => 
      img.id === imageId 
        ? { ...img, customAltText: null }
        : img
    ));
  };

  const handleDoubleClick = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setError('복사되었습니다!');
      setTimeout(() => setError(null), 1500);
    } catch (err) {
      // 복사 실패 시 무시
    }
  };

  const handleExcelDownload = async () => {
    setExcelLoading(true);
    try {
      const statusParam = filter !== 'ALL' ? `&status=${filter}` : '';
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const authHeaders: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

      // 전체 페이지 순차 fetch (size=9999 대신 안정적인 배치 크기 사용)
      let allData: ImageInfo[] = [];
      let fetchPage = 0;
      while (true) {
        const batchRes = await apiClient.get<PageResponse>(
          `/images?page=${fetchPage}&size=999999&sourceType=IMAGE_UPLOAD${statusParam}`
        );
        allData = allData.concat(batchRes.data.content);
        if (fetchPage >= batchRes.data.totalPages - 1 || batchRes.data.content.length === 0) break;
        fetchPage++;
      }
      // 목록에 표시되는 것과 동일하게 altTypeFilter 적용
      const data = altTypeFilter === 'ALL' ? allData : allData.filter(img => img.altGenerationType === altTypeFilter);

      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet('이미지목록');
      ws.columns = [
        { header: 'ID', key: 'id', width: 8 },
        { header: '이미지', key: 'image', width: 14 },
        { header: 'AI 생성 ALT', key: 'alt', width: 50 },
        { header: '생성 방식', key: 'type', width: 15 },
        { header: '상태', key: 'status', width: 12 },
        { header: '최종 수정일', key: 'updatedAt', width: 22 },
      ];
      ws.getRow(1).font = { bold: true };
      ws.getRow(1).height = 18;

      // 업로드 이미지는 자체 서버 → 인증 헤더로 직접 fetch 후 썸네일 삽입
      const imageResults: ({ buffer: ArrayBuffer; extension: 'jpeg' | 'png' | 'gif' } | null)[] = new Array(data.length).fill(null);
      let fetchIdx = 0;
      const fetchWorker = async () => {
        while (fetchIdx < data.length) {
          const i = fetchIdx++;
          const img = data[i];
          const srcUrl = img.displayImageUrl || img.imageSrc;
          if (srcUrl && !srcUrl.startsWith('data:')) {
            imageResults[i] = await fetchImageForExcel(srcUrl, authHeaders);
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(10, data.length) }, fetchWorker));

      for (let i = 0; i < data.length; i++) {
        const img = data[i];
        const row = ws.addRow({
          id: img.id,
          image: '',
          alt: img.generatedAlt ?? '',
          type: img.altGenerationType ?? '',
          status: img.status ?? '',
          updatedAt: new Date(img.updatedAt || img.createdAt).toLocaleString('ko-KR'),
        });
        row.height = 72;
        if (imageResults[i]) {
          try {
            const imageId = workbook.addImage({
              buffer: imageResults[i]!.buffer,
              extension: imageResults[i]!.extension,
            });
            ws.addImage(imageId, { tl: { col: 1, row: i + 1 }, br: { col: 2, row: i + 2 }, editAs: 'oneCell' } as any);
          } catch { /* skip */ }
        }
      }

      const buf = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `이미지업로드_목록_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(dlUrl);
    } catch {
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setExcelLoading(false);
    }
  };

  const fetchStats = async (status: string | null = null, altGenerationType: AltGenerationType | 'ALL' | null = null) => {
    try {
      const statusParam = status && status !== 'ALL' ? `&status=${status}` : '';
      const altTypeParam = altGenerationType && altGenerationType !== 'ALL' ? `&altGenerationType=${altGenerationType}` : '';
      const sourceTypeParam = '&sourceType=IMAGE_UPLOAD';
      const channelParam = '&channel=INNER';
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const res = await apiClient.get<PageResponse>(
        `/images?page=0&size=999999${statusParam}${altTypeParam}${sourceTypeParam}${channelParam}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      // 백엔드 stats 필드 그대로 사용
      if (res.data.stats) {
        setAllStats(res.data.stats);
      }
    } catch {
      // 통계 조회 실패는 무시
    }
  };

  const fetchImages = async (page = 0, status: string | null = null, size = pageSize, sb?: SortBy, sd?: SortDir, altGenerationType?: AltGenerationType | 'ALL' | null, overrideStartDate?: string, overrideEndDate?: string) => {
    setLoading(true);
    try {
      const effectiveSortBy = sb ?? sortBy;
      const effectiveSortDir = sd ?? sortDir;
      const statusParam = status && status !== 'ALL' ? `&status=${status}` : '';
      const effectiveAltType = altGenerationType ?? altTypeFilter;
      const altTypeParam = effectiveAltType && effectiveAltType !== 'ALL' ? `&altGenerationType=${effectiveAltType}` : '';
      const sourceTypeParam = '&sourceType=IMAGE_UPLOAD'; // 이미지 업로드만 필터링
      const channelParam = '&channel=INNER'; // INNER 채널만 필터링
      const sortParam = `&sortBy=${effectiveSortBy}&sortDir=${effectiveSortDir}`;
      const effectiveStartDate = overrideStartDate ?? startDate;
      const effectiveEndDate = overrideEndDate ?? endDate;
      const startDateParam = effectiveStartDate ? `&startDate=${effectiveStartDate}` : '';
      const endDateParam = effectiveEndDate ? `&endDate=${effectiveEndDate}` : '';
      const response = await apiClient.get<PageResponse>(
        `/images?page=${page}&size=${size}${statusParam}${altTypeParam}${sourceTypeParam}${channelParam}${sortParam}${startDateParam}${endDateParam}`
      );
      const normalizedContent = response.data.content.map((img) => ({
        ...img,
        generatedAlt: sanitizeUploadAltError(img.generatedAlt, img.altGenerationType, img.status),
      }));
      setImages(normalizedContent);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements ?? 0);
      setCurrentPage(page);
      if (response.data.sort) setSortIsDefault(response.data.sort.isDefault);
      setError(null); // 성공 시 에러 초기화
      // fetchImages 응답의 stats 재사용 (불필요한 API 호출 제거)
      if (response.data.stats) {
        setAllStats(response.data.stats);
      }
    } catch (err: any) {
      // 404 또는 빈 결과는 에러가 아님
      if (err.response?.status === 404 || err.response?.data?.content?.length === 0) {
        setImages([]);
        setTotalPages(0);
        setError(null);
      } else if (err.code === 'ERR_NETWORK' || err.message.includes('Network Error')) {
        setError('백엔드 서버에 연결할 수 없습니다. 서버가 실행 중인지 확인해주세요.');
      } else {
        setError('이미지 목록을 불러오지 못했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages(0, filter === 'ALL' ? null : filter);
    fetchAiUsage();
    fetchProfileStatus();
  }, []);

  // PENDING 항목이 30초 경과 후 타임아웃 UI로 전환되도록 리렌더 트리거
  useEffect(() => {
    const hasFreshPending = images.some(img => !img.generatedAlt);
    if (!hasFreshPending) return;
    const timer = setTimeout(() => setTick(t => t + 1), 35_000);
    return () => clearTimeout(timer);
  }, [images]);

  // 이미지가 생성된 지 30초 이상 경과하여 PENDING 상태이면 true
  const isLongPending = (createdAt: string): boolean => {
    try { return Date.now() - new Date(createdAt).getTime() > 30_000; }
    catch { return false; }
  };

  const handleFileSelect = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const MAX_FILES = 10;
    
    // 현재 선택된 파일 수 + 새로 추가할 파일 수 체크
    if (selectedFiles.length + fileArray.length > MAX_FILES) {
      setError(`최대 ${MAX_FILES}개의 파일만 선택할 수 있습니다.`);
      return;
    }

    // B/E 권장: jpg/jpeg/jfif/jpe/png/gif/webp → 완전 지원
    //           avif/tiff/svg → 업로드는 되지만 AI 분석 미지원(경고)
    //           bmp/ico → B/E에서 차단 → F/E에서도 거부
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/jfif', 'image/pjpeg',
      'image/png', 'image/gif', 'image/webp',
      'image/tiff', 'image/avif', 'image/svg+xml',
      'application/octet-stream',
    ];
    // AI 분석 미지원 포맷 (업로드는 통과하지만 FAILED 반환될 수 있음)
    const limitedSupportTypes = new Set([
      'image/tiff', 'image/avif', 'image/svg+xml',
    ]);
    const blockedTypes = new Set([
      'image/bmp', 'image/x-icon', 'image/vnd.microsoft.icon',
    ]);
    let errorMessages: string[] = [];
    let limitedWarnings: string[] = [];

    for (const file of fileArray) {
      // 파일 크기 체크 (서버 제한 우선, 없으면 env 설정값)
      const maxBytes = maxAllowedSizeKb > 0 ? maxAllowedSizeKb * 1024 : MAX_UPLOAD_SIZE_MB * 1024 * 1024;
      const maxLabel = maxAllowedSizeKb > 0
        ? (maxAllowedSizeKb >= 1024 ? `${(maxAllowedSizeKb / 1024).toFixed(0)}MB` : `${maxAllowedSizeKb}KB`)
        : MAX_UPLOAD_SIZE_LABEL;
      if (file.size > maxBytes) {
        errorMessages.push(`${file.name}: 파일 크기는 ${maxLabel} 이하여야 합니다.`);
        continue;
      }

      // BMP/ICO: B/E에서 차단 → F/E에서도 거부
      if (blockedTypes.has(file.type)) {
        errorMessages.push(`${file.name}: BMP, ICO 형식은 지원하지 않습니다. JPG, PNG, GIF, WEBP 형식을 사용해 주세요.`);
        continue;
      }

      // 파일 형식 체크
      if (!allowedTypes.includes(file.type)) {
        errorMessages.push(`${file.name}: 지원하지 않는 파일 형식입니다. (JPG, PNG, GIF, WEBP 권장)`);
        continue;
      }

      // AVIF/TIFF/SVG: 업로드는 가능하나 AI 분석 미지원 경고
      if (limitedSupportTypes.has(resolveImageMime(file))) {
        limitedWarnings.push(file.name);
      }

      // 중복 체크
      if (selectedFiles.some(sf => sf.file.name === file.name && sf.file.size === file.size)) {
        continue;
      }

      // 미리보기 생성
      const reader = new FileReader();
      reader.onloadend = () => {
        let dataUrl = reader.result as string;
        // JFIF 등 MIME type이 image/* 가 아닐 경우 올바른 MIME type으로 교체
        // (예: data:application/octet-stream;base64,... → data:image/jpeg;base64,...)
        if (!dataUrl.startsWith('data:image/')) {
          const correctMime = resolveImageMime(file);
          dataUrl = dataUrl.replace(/^data:[^;]*;/, `data:${correctMime};`);
        }
        const fileInfo: SelectedFileInfo = {
          file,
          previewUrl: dataUrl,
          status: 'pending',
          progress: 0,
        };
        setSelectedFiles(prev => [...prev, fileInfo]);
      };
      reader.readAsDataURL(file);
    }

    // 에러가 있으면 표시, 없으면 초기화
    if (errorMessages.length > 0) {
      setError(errorMessages.join('\n'));
    } else if (limitedWarnings.length > 0) {
      setError(
        `⚠️ ${limitedWarnings.join(', ')} — AVIF/TIFF/SVG 형식은 업로드는 가능하지만 AI 분석이 지원되지 않을 수 있습니다. 가능하면 JPG, PNG, WEBP로 변환 후 업로드해 주세요.`
      );
    } else {
      setError(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  // 로컬 모드: B/E가 파일을 저장하고 imageSrc 반환 → 바로 image-register 호출
  const handleUploadLocalMode = async (
    filesToUpload: SelectedFileInfo[],
    imageSrc: string  // 첫 파일의 imageSrc (B/E에서 저장한 경로)
  ) => {
    const results: UploadResult[] = [];

    for (let i = 0; i < filesToUpload.length; i++) {
      const fileInfo = filesToUpload[i];
      const fileIndex = selectedFiles.findIndex(f => f.file === fileInfo.file);

      try {
        // 첫 파일은 imageSrc를 사용, 나머지는 각각 presigned-upload-url 호출
        let registerImageSrc = imageSrc;
        
        if (i > 0) {
          // 나머지 파일들도 presigned-upload-url에 직접 전송
          const file = filesToUpload[i].file;
          const { blob: normalizedBlob, name: normalizedName } = normalizeUploadFile(file);
          const normalizedMime = resolveImageMime(file);
          
          const formData = new FormData();
          formData.append('file', normalizedBlob, normalizedName);
          formData.append('filename', normalizedName);
          formData.append('contentType', normalizedMime);
          
          const res = await apiClient.post<{ imageSrc?: string }>(
            `/presigned-upload-url`,
            formData
          );
          
          if (!res.data?.imageSrc) {
            throw new Error('파일 저장 실패');
          }
          registerImageSrc = res.data.imageSrc;
        }

        // 백엔드에 업로드 완료 등록 + ALT 생성 트리거
        const registerRes = await apiClient.post<UploadResult>(
          `/image-register`,
          null,
          {
            params: {
              imageSrc: registerImageSrc,
              originalFilename: filesToUpload[i].file.name,
              useContextProfile: useContextProfile.toString(),
            }
          }
        );

        results.push(registerRes.data);
        setSelectedFiles(prev => prev.map((f, idx) =>
          idx === fileIndex ? { ...f, status: 'completed' as const, progress: 100, result: registerRes.data } : f
        ));

        // SSE 연결로 실시간 상태 수신
        if (registerRes.data.id) {
          sseService.connect(registerRes.data.id, {
            onStatusUpdate: (data) => {
              if (data.status === 'FAILED' && data.errorType) {
                // 에러 코드에 따른 알림 표시
                let errorMessage = '이미지 분석에 실패했습니다.';
                if (data.errorType === 'MONTHLY_LIMIT_EXCEEDED') {
                  errorMessage = '월별 한도를 초과했습니다. 요금제 업그레이드를 진행해 주세요.';
                } else if (data.errorType === 'DAILY_LIMIT_EXCEEDED') {
                  errorMessage = '일별 한도를 초과했습니다. 내일 다시 시도해주세요.';
                } else if (data.errorType === 'ENTERPRISE_LIMIT_EXCEEDED') {
                  errorMessage = '엔터프라이즈 한도를 초과했습니다. 엔터프라이즈 계약서 수정으로 이용가능한 건수를 추가해 주세요.';
                }
                alert(errorMessage);
              }

              setSelectedFiles(prev => prev.map((f, idx) =>
                idx === fileIndex ? {
                  ...f,
                  result: {
                    ...f.result!,
                    status: data.status,
                    altGenerationType: (data.altGenerationType as AltGenerationType) || f.result!.altGenerationType,
                    generatedAlt: data.generatedAlt || f.result!.generatedAlt
                  }
                } : f
              ));
            },
            onTimeout: () => {
              sseService.disconnect();
            },
            onError: () => {
              sseService.disconnect();
            }
          });
        }
      } catch (err: any) {
        const errMsg = err.response?.data?.message || '이미지 업로드 오류';
        setSelectedFiles(prev => prev.map((f, idx) =>
          idx === fileIndex ? { ...f, status: 'failed' as const, error: errMsg } : f
        ));
      }
    }

    const successCount = results.length;
    if (successCount > 0) {
      setUploadSuccess(`✅ ${successCount}개 이미지 업로드 완료! ALT 텍스트를 백그라운드에서 생성 중입니다. (약 10초 소요)`);
      const uploadedIds = results.map(r => r.id).filter(id => id != null);
      setTimeout(async () => {
        fetchImages(0, filter === 'ALL' ? null : filter);
        if (uploadedIds.length > 0) {
          try {
            const res = await apiClient.get<PageResponse>(`/images?page=0&size=100&sourceType=IMAGE_UPLOAD`);
            const updatedImagesMap = new Map(
              res.data.content
                .filter(img => uploadedIds.includes(img.id))
                .map(img => [img.id, img])
            );
            setSelectedFiles(prev => prev.map(file => {
              if (file.status !== 'completed' || !file.result) return file;
              const updatedImage = updatedImagesMap.get(file.result.id);
              if (updatedImage) {
                return {
                  ...file,
                  result: {
                    ...file.result,
                    status: updatedImage.status,
                    altGenerationType: updatedImage.altGenerationType,
                    generatedAlt: sanitizeUploadAltError(
                      updatedImage.generatedAlt,
                      updatedImage.altGenerationType,
                      updatedImage.status
                    )
                  }
                };
              }
              return file;
            }));

            // 모든 파일의 ALT 생성이 완료되면 메시지 제거
            const allCompleted = res.data.content
              .filter(img => uploadedIds.includes(img.id))
              .every(img => img.status === 'COMPLETED' || img.generatedAlt);

            if (allCompleted) {
              setUploadSuccess('');
            }
          } catch (err) {
            console.error('업로드 파일 상태 업데이트 실패:', err);
          }
        }
      }, 10000);
    }
  };

  // 운영 환경 전용: Presigned URL → S3 직접 PUT → image-register 3단계 업로드
  const handleUploadPresigned = async (
    filesToUpload: SelectedFileInfo[],
    firstPresignedData: { uploadUrl: string; s3Key: string; finalUrl: string }
  ) => {
    const allPresignedData: Array<{ uploadUrl: string; s3Key: string; finalUrl: string }> = [firstPresignedData];

    // 나머지 파일의 Presigned URL 발급
    for (let i = 1; i < filesToUpload.length; i++) {
      const file = filesToUpload[i].file;
      const res = await apiClient.post<{ uploadUrl: string; s3Key: string; finalUrl: string }>(
        `/presigned-upload-url`,
        null,
        { params: { filename: file.name, contentType: file.type || 'application/octet-stream' } }
      );
      allPresignedData.push(res.data);
    }

    const results: UploadResult[] = [];

    for (let i = 0; i < filesToUpload.length; i++) {
      const fileInfo = filesToUpload[i];
      const { uploadUrl, s3Key, finalUrl } = allPresignedData[i];
      const fileIndex = selectedFiles.findIndex(f => f.file === fileInfo.file);

      try {
        // S3에 직접 업로드 (CloudFront 거치지 않음)
        // JFIF 등 비표준 MIME/확장자를 정규화하여 B/E AI 파이프라인이 처리 가능하도록 함
        const { blob: normalizedBlob, name: normalizedFileName } = normalizeUploadFile(fileInfo.file);
        const fileContentType = resolveImageMime(fileInfo.file);
        // s3Client: 인터셉터/기본헤더 없는 전용 인스턴스 - Authorization 헤더 전송 방지
        console.log('[S3 Upload v4] 정규화된 이미지 업로드 ✅', { original: fileInfo.file.name, normalized: normalizedFileName, contentType: fileContentType });
        await s3Client.put(uploadUrl, normalizedBlob, {
          headers: { 'Content-Type': fileContentType },
          transformRequest: [(data) => data],
          onUploadProgress: (progressEvent) => {
            const progress = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 50;
            setSelectedFiles(prev => prev.map((f, idx) =>
              idx === fileIndex ? { ...f, progress } : f
            ));
          },
        });

        // 백엔드에 업로드 완료 등록 + ALT 생성 트리거
        const registerRes = await apiClient.post<UploadResult>(
          `/image-register`,
          null,
          {
            params: {
              s3Key,
              finalUrl,
              originalFilename: normalizedFileName,
              useContextProfile: useContextProfile.toString(),
            }
          }
        );

        results.push(registerRes.data);
        setSelectedFiles(prev => prev.map((f, idx) =>
          idx === fileIndex ? { ...f, status: 'completed' as const, progress: 100, result: registerRes.data } : f
        ));

        // SSE 연결로 실시간 상태 수신
        if (registerRes.data.id) {
          sseService.connect(registerRes.data.id, {
            onStatusUpdate: (data) => {
              if (data.status === 'FAILED' && data.errorType) {
                // 에러 코드에 따른 알림 표시
                let errorMessage = '이미지 분석에 실패했습니다.';
                if (data.errorType === 'MONTHLY_LIMIT_EXCEEDED') {
                  errorMessage = '월별 한도를 초과했습니다. 요금제 업그레이드를 진행해 주세요.';
                } else if (data.errorType === 'DAILY_LIMIT_EXCEEDED') {
                  errorMessage = '일별 한도를 초과했습니다. 내일 다시 시도해주세요.';
                } else if (data.errorType === 'ENTERPRISE_LIMIT_EXCEEDED') {
                  errorMessage = '엔터프라이즈 한도를 초과했습니다. 엔터프라이즈 계약서 수정으로 이용가능한 건수를 추가해 주세요.';
                }
                alert(errorMessage);
              }

              setSelectedFiles(prev => prev.map((f, idx) =>
                idx === fileIndex ? {
                  ...f,
                  result: {
                    ...f.result!,
                    status: data.status,
                    altGenerationType: (data.altGenerationType as AltGenerationType) || f.result!.altGenerationType,
                    generatedAlt: data.generatedAlt || f.result!.generatedAlt
                  }
                } : f
              ));
            },
            onTimeout: () => {
              sseService.disconnect();
            },
            onError: () => {
              sseService.disconnect();
            }
          });
        }
      } catch (err: any) {
        const errMsg = err.response?.data?.message || 'S3 업로드 오류';
        setSelectedFiles(prev => prev.map((f, idx) =>
          idx === fileIndex ? { ...f, status: 'failed' as const, error: errMsg } : f
        ));
      }
    }

    const successCount = results.length;
    if (successCount > 0) {
      setUploadSuccess(`✅ ${successCount}개 이미지 업로드 완료! ALT 텍스트를 백그라운드에서 생성 중입니다. (약 10초 소요)`);
      const uploadedIds = results.map(r => r.id).filter(id => id != null);
      setTimeout(async () => {
        fetchImages(0, filter === 'ALL' ? null : filter);
        if (uploadedIds.length > 0) {
          try {
            const res = await apiClient.get<PageResponse>(`/images?page=0&size=100&sourceType=IMAGE_UPLOAD`);
            const updatedImagesMap = new Map(
              res.data.content
                .filter(img => uploadedIds.includes(img.id))
                .map(img => [img.id, img])
            );
            setSelectedFiles(prev => prev.map(file => {
              if (file.status !== 'completed' || !file.result) return file;
              const updatedImage = updatedImagesMap.get(file.result.id);
              if (updatedImage) {
                return {
                  ...file,
                  result: {
                    ...file.result,
                    status: updatedImage.status,
                    altGenerationType: updatedImage.altGenerationType,
                    generatedAlt: sanitizeUploadAltError(
                      updatedImage.generatedAlt,
                      updatedImage.altGenerationType,
                      updatedImage.status
                    )
                  }
                };
              }
              return file;
            }));

            // 모든 파일의 ALT 생성이 완료되면 메시지 제거
            const allCompleted = res.data.content
              .filter(img => uploadedIds.includes(img.id))
              .every(img => img.status === 'COMPLETED' || img.generatedAlt);

            if (allCompleted) {
              setUploadSuccess('');
            }
          } catch (err) {
            console.error('업로드 파일 상태 업데이트 실패:', err);
          }
        }
      }, 10000);
    }
  };

  const handleUpload = async () => {
    console.log('[handleUpload] 호출됨. selectedFiles:', selectedFiles.length, selectedFiles.map(f => ({ name: f.file.name, status: f.status })));

    if (selectedFiles.length === 0) {
      setError('파일을 선택해주세요.');
      return;
    }

    // 무료 플랜 일일 한도 체크
    if (aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0)) {
      alert(`무료 플랜은 하루 최대 ${aiUsage.dailyLimit}개의 ALT 텍스트를 생성할 수 있습니다.\n제한 없이 이용하시려면 스타트 플랜 이상으로 업그레이드해 주세요.`);
      return;
    }
    if (aiUsage) {
      // 팀 컨텍스트인 경우 팀 한도 기준으로 체크
      if (aiUsage.isTeamContext) {
        const teamLimit = aiUsage.teamLimit ?? aiUsage.limit;
        const teamUsed = aiUsage.teamTotalUsed ?? aiUsage.used;
        if (teamUsed >= teamLimit) {
          console.log('[handleUpload] 팀 AI 사용 한도 초과:', aiUsage);
          setError(`팀 AI 사용 한도(${teamLimit}건)를 초과하였습니다. 플랜 업그레이드 또는 관리자에게 문의해 주세요.`);
          return;
        }
      } else if (!aiUsage.canUse) {
        console.log('[handleUpload] AI 사용 한도 초과:', aiUsage);
        setError(`이번 달 AI 사용 한도(${aiUsage.limit}건)를 초과하였습니다. 플랜 업그레이드 또는 관리자에게 문의해 주세요.`);
        return;
      }
    }

    setUploading(true);
    setError(null);
    setUploadSuccess(null);

    // 업로드할 파일 필터링 (pending 상태만)
    const filesToUpload = selectedFiles.filter(
      f => f.status === 'pending'
    );

    console.log('[handleUpload] filesToUpload:', filesToUpload.length);

    if (filesToUpload.length === 0) {
      console.log('[handleUpload] pending 파일 없음 - 조기 종료');
      setError('업로드할 파일이 없습니다.');
      setUploading(false);
      return;
    }

    try {
      // presigned-upload-url: filename + contentType만 query param으로 전송 (파일 본문 제외)
      // → WAF body 크기 제한(8KB) 초과 차단 방지
      try {
        const firstFile = filesToUpload[0].file;
        const { name: firstNormalizedName } = normalizeUploadFile(firstFile);
        const firstNormalizedMime = resolveImageMime(firstFile);
        
        const presignedRes = await apiClient.post<{ 
          s3Enabled?: boolean; 
          uploadUrl?: string; 
          s3Key?: string; 
          finalUrl?: string;
          imageSrc?: string;  // 로컬 모드: B/E가 파일을 저장하고 imageSrc 반환
        }>(
          `/presigned-upload-url?filename=${encodeURIComponent(firstNormalizedName)}&contentType=${encodeURIComponent(firstNormalizedMime)}`
        );

        // CloudFront/프록시가 S3 HTML을 반환한 경우 감지 (캐시 오류)
        const presignedRaw = presignedRes.data as any;
        if (typeof presignedRaw === 'string' && (presignedRaw.trimStart().startsWith('<!') || presignedRaw.trimStart().startsWith('<html'))) {
          throw new Error('서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요. (지속 발생 시 관리자에게 문의하세요)');
        }

        // 로컬 모드: imageSrc 반환 (파일이 B/E에 저장됨)
        if (presignedRes.data?.imageSrc) {
          setSelectedFiles(prev => prev.map(f =>
            f.status === 'pending' ? { ...f, status: 'uploading' as const, progress: 10 } : f
          ));
          await handleUploadLocalMode(filesToUpload, presignedRes.data.imageSrc);
          return;
        }

        // 운영 환경: Presigned URL 반환
        if (presignedRes.data?.s3Enabled !== false && presignedRes.data?.uploadUrl) {
          setSelectedFiles(prev => prev.map(f =>
            f.status === 'pending' ? { ...f, status: 'uploading' as const, progress: 10 } : f
          ));
          await handleUploadPresigned(
            filesToUpload,
            presignedRes.data as { uploadUrl: string; s3Key: string; finalUrl: string }
          );
          return;
        }
        // s3Enabled: false → 로컬 환경, 아래 multipart 방식으로 계속
      } catch (presignedErr: any) {
        const status = presignedErr.response?.status;
        if (!status || status === 404 || status >= 500) {
          // endpoint 없음(404) 또는 서버 미구현(500) → multipart 방식으로 계속
        } else if (status === 400) {
          // 400: 로컬 환경에서는 S3 미설정으로 400 반환 → multipart fallback으로 계속
          // 운영 환경에서 파일 형식 거부 시에도 multipart 엔드포인트에서 동일하게 거부됨
        }
        // 401/403/413 등 → multipart 방식(image-upload-multiple)으로 계속
      }

      // 다중 업로드 API 사용 (로컬 환경 fallback)
      const formData = new FormData();
      filesToUpload.forEach(fileInfo => {
        // JFIF 등 MIME type 미지원 파일을 JPEG로 정규화하여 백엔드 AI 파이프라인이 처리 가능하도록 함
        const { blob, name } = normalizeUploadFile(fileInfo.file);
        formData.append('files', blob, name);
      });
      formData.append('useContextProfile', useContextProfile.toString());

      // 모든 파일을 uploading 상태로 변경
      setSelectedFiles(prev => prev.map(f => 
        f.status === 'pending' ? { ...f, status: 'uploading' as const, progress: 50 } : f
      ));

      const response = await apiClient.post<{
        success: number;
        failed: number;
        total: number;
        results: UploadResult[];
      }>(
        `/image-upload-multiple`,
        formData
      );

      // 응답 구조 정규화 - 운영/개발 환경에 따라 다를 수 있는 3가지 형식 처리:
      //   1. {success, failed, total, results: [...]}  ← 개발 환경 배치 응답
      //   2. [{id, imageSrc, ...}, ...]               ← 운영 환경 배열 응답
      //   3. {id, imageSrc, ...}                      ← 단일 객체 응답
      // 응답 타입을 any로 받아 형식에 무관하게 처리
      // eslint-disable-next-line
      const rawData = response.data as any;

      // HTML 응답 감지: CloudFront/프록시가 /api/crawler/image-upload-multiple 요청을
      // 백엔드로 라우팅하지 않고 SPA index.html을 반환하는 경우
      if (typeof rawData === 'string' && (rawData.trimStart().startsWith('<!') || rawData.trimStart().startsWith('<html'))) {
        throw new Error('서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요. (지속 발생 시 관리자에게 문의하세요)');
      }

      // 503 또는 UPLOAD_TEMPORARILY_DISABLED 응답 처리
      if (response.status === 503 || rawData?.code === 'UPLOAD_TEMPORARILY_DISABLED') {
        if (rawData?.maxAllowedSizeKb > 0) {
          setMaxAllowedSizeKb(rawData.maxAllowedSizeKb);
        }
        const errMsg = rawData?.message || '현재 업로드가 일시적으로 비활성화되어 있습니다. 잠시 후 다시 시도해 주세요.';
        throw new Error(errMsg);
      }

      let results: UploadResult[] = [];
      let batchSuccess: number | undefined;
      let batchFailed: number | undefined;

      if (Array.isArray(rawData)) {
        // 형식 2: 배열 직접 반환
        results = rawData as UploadResult[];
      } else if (rawData?.results !== undefined) {
        // 형식 1: 래퍼 객체
        results = rawData.results ?? [];
        batchSuccess = rawData.success;
        batchFailed = rawData.failed;
      } else if (rawData?.id !== undefined) {
        // 형식 3: 단일 객체
        results = [rawData as UploadResult];
      }

      // 결과에 따라 상태 업데이트
      let uploadingIndex = 0;
      setSelectedFiles(prev => prev.map(f => {
        if (f.status !== 'uploading') return f;
        
        const result = results[uploadingIndex] ?? null;
        uploadingIndex++;
        
        return {
          ...f,
          status: 'completed' as const,
          progress: 100,
          ...(result ? { result } : {}),
        };
      }));

      // 성공/실패 메시지
      if (typeof batchFailed === 'number' && batchFailed > 0) {
        setError(`${batchSuccess}개 완료, ${batchFailed}개 오류`);
      } else {
        setError(null);
      }

      // 업로드 성공 안내
      // 운영환경에서 응답 구조가 다를 수 있으므로 우선순위대로 fallback:
      //   1. batchSuccess (배치 응답의 success 카운트)
      //   2. results.length (정규화된 결과 배열 길이)
      //   3. filesToUpload.length (실제로 업로드 시도한 파일 수 - 최후 fallback)
      const successCount = (typeof batchSuccess === 'number' && batchSuccess > 0)
        ? batchSuccess
        : results.length > 0
        ? results.length
        : filesToUpload.length;

      if (successCount > 0) {
        setUploadSuccess(`✅ ${successCount}개 이미지 업로드 완료! ALT 텍스트를 백그라운드에서 생성 중입니다. (약 10초 소요)`);
        
        // 업로드된 파일 ID 목록 저장
        const uploadedIds = results.map(r => r.id).filter(id => id != null);
        
        // 10초 후 자동 새로고침 및 업로드된 파일 상태 업데이트
        setTimeout(async () => {
          fetchImages(0, filter === 'ALL' ? null : filter);
          
          if (uploadedIds.length > 0) {
            try {
              const res = await apiClient.get<PageResponse>(
                `/images?page=0&size=100&sourceType=IMAGE_UPLOAD`
              );
              
              const updatedImagesMap = new Map(
                res.data.content
                  .filter(img => uploadedIds.includes(img.id))
                  .map(img => [img.id, img])
              );
              
              setSelectedFiles(prev => prev.map(file => {
                if (file.status !== 'completed' || !file.result) return file;

                const updatedImage = updatedImagesMap.get(file.result.id);

                if (updatedImage) {
                  return {
                    ...file,
                    result: {
                      ...file.result,
                      status: updatedImage.status,
                      altGenerationType: updatedImage.altGenerationType,
                      generatedAlt: sanitizeUploadAltError(
                        updatedImage.generatedAlt,
                        updatedImage.altGenerationType,
                        updatedImage.status
                      )
                    }
                  };
                }

                return file;
              }));

              // 모든 파일의 ALT 생성이 완료되면 메시지 제거
              const allCompleted = res.data.content
                .filter(img => uploadedIds.includes(img.id))
                .every(img => img.status === 'COMPLETED' || img.generatedAlt);

              if (allCompleted) {
                setUploadSuccess('');
              }
            } catch (err) {
              console.error('업로드 파일 상태 업데이트 실패:', err);
            }
          }
        }, 10000);
      }

    } catch (err: any) {
      // 다중 업로드 실패 시 단일 업로드로 fallback
      console.error('[handleUpload] catch 진입:', { code: err.code, status: err.response?.status, message: err.message });

      if (err.response?.status === 404) {
        console.log('다중 업로드 API 없음. 단일 업로드로 전환...');
        // fallback 시 현재 파일 상태를 다시 pending으로 복원 (handleUploadSingle이 pending을 필터링함)
        setSelectedFiles(prev => prev.map(f =>
          f.status === 'uploading' ? { ...f, status: 'pending' as const, progress: 0 } : f
        ));
        await handleUploadSingle();
        return;
      }

      if (err.response?.status === 429) {
        if (aiUsage?.plan === 'FREE') {
          setSelectedFiles(prev => prev.map(f =>
            f.status === 'uploading' ? { ...f, status: 'failed' as const, error: '일일 한도 초과' } : f
          ));
          fetchAiUsage();
          alert(`무료 플랜은 하루 최대 20개의 ALT 텍스트를 생성할 수 있습니다.\n제한 없이 이용하시려면 스타트 플랜 이상으로 업그레이드해 주세요.`);
        } else {
          const errMsg = err.response?.data?.message || '이번 달 AI 사용 한도를 초과하였습니다. 플랜 업그레이드 또는 관리자에게 문의해 주세요.';
          setError(errMsg);
          setSelectedFiles(prev => prev.map(f =>
            f.status === 'uploading' ? { ...f, status: 'failed' as const, error: errMsg } : f
          ));
          fetchAiUsage();
        }
        return;
      }

      if (err.response?.status === 503 || err.response?.data?.code === 'UPLOAD_TEMPORARILY_DISABLED') {
        const errMsg = err.response?.data?.message || '현재 업로드가 일시적으로 비활성화되어 있습니다. 잠시 후 다시 시도해 주세요.';
        if (err.response?.data?.maxAllowedSizeKb > 0) {
          setMaxAllowedSizeKb(err.response.data.maxAllowedSizeKb);
        }
        setError(errMsg);
        setSelectedFiles(prev => prev.map(f =>
          f.status === 'uploading' ? { ...f, status: 'failed' as const, error: errMsg } : f
        ));
        return;
      }

      let errorMsg = '업로드 중 오류가 발생했습니다';
      const status = err.response?.status;
      if (err.message?.includes('API 라우팅 오류')) {
        errorMsg = err.message;
      } else if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
        errorMsg = `[네트워크 오류] 서버에 도달하지 못했습니다. AWS nginx의 client_max_body_size 설정 또는 CORS 정책을 확인해 주세요.`;
      } else if (status === 413) {
        errorMsg = `[413 오류] 파일 크기 초과 - 서버(nginx) 업로드 용량 제한을 초과했습니다. nginx client_max_body_size 설정을 확인해 주세요.`;
      } else if (status === 0 || err.message?.includes('CORS')) {
        errorMsg = `[CORS 오류] 서버의 CORS 설정에서 multipart/form-data POST 요청이 허용되지 않습니다.`;
      } else if (status) {
        const serverMsg = err.response?.data?.message || err.response?.data || '';
        errorMsg = `[HTTP ${status}] 업로드 오류${serverMsg ? ': ' + serverMsg : ''}`;
      } else {
        errorMsg = `업로드 오류: ${err.message || '알 수 없는 오류'}`;
      }

      console.error('[Upload Error]', { code: err.code, status, message: err.message, response: err.response?.data });
      
      setError(errorMsg);
      setSelectedFiles(prev => prev.map(f => 
        f.status === 'uploading' ? { ...f, status: 'failed' as const, error: errorMsg } : f
      ));
    } finally {
      setUploading(false);
      fetchImages(0, filter === 'ALL' ? null : filter);
    }
  };

  // Fallback: 단일 업로드 (다중 업로드 API 없을 때)
  const handleUploadSingle = async () => {
    const filesToUpload = selectedFiles.filter(f => f.status === 'pending');

    for (let i = 0; i < filesToUpload.length; i++) {
      const fileIndex = selectedFiles.findIndex(f => f.file === filesToUpload[i].file);
      
      setSelectedFiles(prev => prev.map((f, idx) => 
        idx === fileIndex ? { ...f, status: 'uploading' as const, progress: 0 } : f
      ));

      try {
        const formData = new FormData();
        // JFIF 등 MIME type 미지원 파일을 JPEG로 정규화
        const { blob: uploadBlob, name: uploadName } = normalizeUploadFile(filesToUpload[i].file);
        formData.append('file', uploadBlob, uploadName);
        formData.append('useContextProfile', useContextProfile.toString());

        const response = await apiClient.post<UploadResult>(
          `/image-upload`,
          formData,
          {
            onUploadProgress: (progressEvent) => {
              const progress = progressEvent.total 
                ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
                : 0;
              setSelectedFiles(prev => prev.map((f, idx) => 
                idx === fileIndex ? { ...f, progress } : f
              ));
            },
          }
        );

        setSelectedFiles(prev => prev.map((f, idx) => 
          idx === fileIndex ? { 
            ...f, 
            status: 'completed' as const, 
            progress: 100,
            result: response.data 
          } : f
        ));
      } catch (err: any) {
        let errorMsg = '업로드 중 오류가 발생했습니다';
        if (err.code === 'ERR_NETWORK' || err.message.includes('Network Error')) {
          errorMsg = '서버 연결 오류';
        } else {
          errorMsg = err.response?.data?.message || '업로드 중 오류가 발생했습니다';
        }

        setSelectedFiles(prev => prev.map((f, idx) => 
          idx === fileIndex ? { 
            ...f, 
            status: 'failed' as const,
            error: errorMsg 
          } : f
        ));
      }
    }

    // 단일 업로드 완료 안내
    setUploadSuccess('✅ 이미지 업로드 완료! ALT 텍스트를 백그라운드에서 생성 중입니다. (약 10초 소요)');

    setTimeout(async () => {
      await fetchImages(0, filter === 'ALL' ? null : filter);

      // 업로드된 파일의 ALT 생성이 완료되면 메시지 제거
      const uploadedFile = selectedFiles.find(f => f.status === 'completed' && f.result);
      if (uploadedFile?.result?.id) {
        try {
          const res = await apiClient.get<PageResponse>(`/images?page=0&size=100&sourceType=IMAGE_UPLOAD`);
          const updatedImage = res.data.content.find(img => img.id === uploadedFile.result!.id);
          if (updatedImage && (updatedImage.status === 'COMPLETED' || updatedImage.generatedAlt)) {
            setUploadSuccess('');
          }
        } catch (err) {
          console.error('업로드 파일 상태 확인 실패:', err);
        }
      }
    }, 10000);
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, idx) => idx !== index));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClearSelection = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('업로드한 모든 이미지 데이터를 삭제하시겠습니까?\n(업로드된 파일도 함께 삭제됩니다)')) {
      return;
    }

    try {
      await apiClient.delete(`/images/clear?sourceType=IMAGE_UPLOAD`);
      setSelectedFiles([]);
      setImages([]);
      setTotalPages(0);
      setTotalElements(0);
      setCurrentPage(0);
      setError(null);
      alert('모든 데이터가 삭제되었습니다.');
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('삭제 API가 구현되지 않았습니다. 백엔드 개발이 필요합니다.');
      } else {
        setError('데이터를 삭제하지 못했습니다.');
      }
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    if (!window.confirm('이 이미지를 삭제하시겠습니까? (업로드된 파일도 함께 삭제됩니다)')) {
      return;
    }

    try {
      await apiClient.delete(`/images/${imageId}`);
      fetchImages(currentPage, filter === 'ALL' ? null : filter);
      alert('이미지가 삭제되었습니다.');
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('삭제 API가 구현되지 않았습니다. 백엔드 개발이 필요합니다.');
      } else {
        setError('이미지를 삭제하지 못했습니다.');
      }
    }
  };

  const handleRegenerateAlt = async (imageId: number, forceRefresh = false) => {
    if (aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0)) {
      setError(`⚠️ 오늘 ${aiUsage.dailyUsed ?? 0}/${aiUsage.dailyLimit}건 — 일일 한도 초과. 내일 다시 이용하거나 플랜을 업그레이드하세요.`);
      return;
    }
    
    // forceRefresh인 경우 새로운 엔드포인트 사용
    if (forceRefresh) {
      if (!window.confirm('이미 AI로 생성한 ALT를 다시 생성하여 ALT를 업데이트 하시겠습니까? (AI 사용량이 차감됩니다)')) {
        return;
      }

      setRegeneratingIds(prev => new Set(prev).add(imageId));
      try {
        const response = await apiClient.post(`/images/${imageId}/force-refresh`);

        // SSE 연결로 실시간 상태 수신
        sseService.connect(imageId, {
          onConnected: (data) => {
            console.log('[SSE] 강제 재분석 연결됨:', data);
          },
          onStatusUpdate: (data) => {
            if (data.status === 'FAILED' && data.errorType) {
              // 에러 코드에 따른 알림 표시
              let errorMessage = '이미지 분석에 실패했습니다.';
              if (data.errorType === 'MONTHLY_LIMIT_EXCEEDED') {
                errorMessage = '월별 한도를 초과했습니다. 요금제 업그레이드를 진행해 주세요.';
              } else if (data.errorType === 'DAILY_LIMIT_EXCEEDED') {
                errorMessage = '일별 한도를 초과했습니다. 내일 다시 시도해주세요.';
              } else if (data.errorType === 'ENTERPRISE_LIMIT_EXCEEDED') {
                errorMessage = '엔터프라이즈 한도를 초과했습니다. 엔터프라이즈 계약서 수정으로 이용가능한 건수를 추가해 주세요.';
              }
              alert(errorMessage);
            }

            if (data.status === 'COMPLETED' || data.status === 'FAILED') {
              fetchImages(currentPage, filter === 'ALL' ? null : filter);
              fetchAiUsage();
              setRegeneratingIds(prev => {
                const next = new Set(prev);
                next.delete(imageId);
                return next;
              });
              sseService.disconnect();
            }
          },
          onTimeout: (data) => {
            console.log('[SSE] 타임아웃:', data);
            fetchImages(currentPage, filter === 'ALL' ? null : filter);
            fetchAiUsage();
            setRegeneratingIds(prev => {
              const next = new Set(prev);
              next.delete(imageId);
              return next;
            });
          },
          onError: (error) => {
            console.error('[SSE] 에러:', error);
            fetchImages(currentPage, filter === 'ALL' ? null : filter);
            fetchAiUsage();
            setRegeneratingIds(prev => {
              const next = new Set(prev);
              next.delete(imageId);
              return next;
            });
          }
        });
      } catch (err: any) {
        alert(err.response?.data?.error || 'ALT 강제 재생성 실패');
        setRegeneratingIds(prev => {
          const next = new Set(prev);
          next.delete(imageId);
          return next;
        });
      }
      return;
    }
    
    // 기존 재분석 로직
    const confirmMessage = 'AI를 통한 ALT 생성을 다시 시도하시겠습니까?';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    // 로딩 상태 추가
    setRegeneratingIds(prev => new Set(prev).add(imageId));

    try {
      const response = await apiClient.post(`/images/${imageId}/regenerate`);

      // SSE 연결로 실시간 상태 수신
      sseService.connect(imageId, {
        onConnected: (data) => {
          console.log('[SSE] 재분석 연결됨:', data);
        },
        onStatusUpdate: (data) => {
          if (data.status === 'COMPLETED' || data.status === 'FAILED') {
            fetchImages(currentPage, filter === 'ALL' ? null : filter);
            setRegeneratingIds(prev => {
              const next = new Set(prev);
              next.delete(imageId);
              return next;
            });
            sseService.disconnect();
          }
        },
        onTimeout: (data) => {
          console.log('[SSE] 타임아웃:', data);
          fetchImages(currentPage, filter === 'ALL' ? null : filter);
          setRegeneratingIds(prev => {
            const next = new Set(prev);
            next.delete(imageId);
            return next;
          });
        },
        onError: (error) => {
          console.error('[SSE] 에러:', error);
          fetchImages(currentPage, filter === 'ALL' ? null : filter);
          setRegeneratingIds(prev => {
            const next = new Set(prev);
            next.delete(imageId);
            return next;
          });
        }
      });
    } catch (err: any) {
      console.error('재분석 에러:', err);
      
      // 로딩 상태 제거
      setRegeneratingIds(prev => {
        const next = new Set(prev);
        next.delete(imageId);
        return next;
      });
      
      let errorMessage = '재분석 중 오류가 발생했습니다.';
      
      if (err.response?.data) {
        // 백엔드의 새로운 에러 응답 형식 (400/503)
        const { message, errorType } = err.response.data;
        
        if (message) {
          errorMessage = `❌ AI 분석 오류\n\n${message}`;
        } else {
          // 레거시 응답 처리
          const errorData = typeof err.response.data === 'string' 
            ? err.response.data 
            : JSON.stringify(err.response.data);
          errorMessage = `❌ 재분석 오류\n\n${errorData.substring(0, 200)}`;
        }
        
        // 에러 타입별 추가 로깅 (선택사항)
        if (errorType) {
          console.error('에러 타입:', errorType);
        }
      } else if (err.code === 'ERR_NETWORK') {
        errorMessage = '❌ 서버 연결 오류\n\n백엔드 서버가 실행 중인지 확인해주세요.';
      }
      
      setError(errorMessage);
      alert(errorMessage);
      
      // 실패 상태 반영을 위해 새로고침
      fetchImages(currentPage, filter === 'ALL' ? null : filter);
    }
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { className: string; icon: any; label: string }> = {
      PENDING: { className: 'bg-gray-200 text-gray-800', icon: Clock, label: '대기중' },
      COMPLETED: { className: 'bg-green-200 text-green-800', icon: CheckCircle2, label: '완료' },
      FAILED: { className: 'bg-red-200 text-red-800', icon: AlertCircle, label: '오류' }
    };

    const entry = config[status] || { className: 'bg-gray-200 text-gray-800', icon: Clock, label: status || '알수없음' };
    const { className, icon: Icon, label } = entry;

    return (
      <Badge className={`flex items-center gap-1 w-fit ${className}`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const getAltTypeBadge = (type: AltGenerationType) => {
    const config: Record<string, { className: string; icon: any; label: string }> = {
      AI: { className: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle2, label: '✓ AI 분석' },
      FALLBACK: { className: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: AlertCircle, label: '⚠ 임시 생성' },
      ERROR: { className: 'bg-red-100 text-red-800 border-red-300', icon: AlertCircle, label: '✗ 생성 오류' },
      PENDING: { className: 'bg-gray-100 text-gray-800 border-gray-300', icon: Clock, label: '📝 미생성' },
      SKIPPED_DECORATIVE: { className: 'bg-purple-100 text-purple-800 border-purple-300', icon: AlertCircle, label: '🎨 장식용 이미지' }
    };

    const entry = config[type] || { className: 'bg-gray-100 text-gray-800 border-gray-300', icon: Clock, label: type || '처리중' };
    const { className, icon: Icon, label } = entry;

    return (
      <Badge className={`flex items-center gap-1 w-fit border ${className}`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const getAltTypeStats = () => {
    if (!allStats) {
      return { ai: 0, fallback: 0, pending: 0, error: 0, skippedDecorative: 0, total: 0, aiPercentage: 0 };
    }
    return allStats;
  };

  const getDisplayAltText = (
    generatedAlt: string | null,
    altType: AltGenerationType,
    status: string
  ) => {
    return sanitizeUploadAltError(generatedAlt, altType, status) || '';
  };

  const filteredImages = images;

  return (
    <PageContainer maxWidth="full">
      <PageHeader 
        title={menuName || '이미지 업로드'}
        description="PC에 저장된 이미지를 업로드하여, 즉시 ALT 텍스트를 생성합니다"
        icon={menuIcon}
        action={
          <Button variant="outline" onClick={() => setShowGuide(!showGuide)} className="shrink-0">
            <HelpCircle className="w-4 h-4 mr-2" />
            사용 가이드
          </Button>
        }
      />
      <div className="space-y-8">
        <Card>
          <CardContent className="space-y-6 pt-6">
            {/* AI 사용량 표시 */}
            {aiUsage && (
              <div className={`flex flex-wrap items-center justify-between gap-2 px-4 py-2 rounded-lg text-sm border ${
                aiUsage.isTeamContext
                  ? (aiUsage.teamTotalUsed !== null && aiUsage.teamLimit !== null && aiUsage.teamTotalUsed >= aiUsage.teamLimit)
                    ? 'bg-red-50 border-red-300'
                    : 'bg-blue-50 border-blue-200'
                  : (!aiUsage.canUse ? 'bg-red-50 border-red-300' :
                  (aiUsage.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))
                    ? 'bg-orange-50 border-orange-300'
                    : 'bg-gray-50 border-gray-200')
              }`}>
                <span className="text-gray-700">
                  {aiUsage.isTeamContext ? (
                    <>
                      {aiUsage.teamTotalUsed !== null && aiUsage.teamLimit !== null ? (
                        <>
                          팀 사용량: <strong>{aiUsage.teamTotalUsed.toLocaleString()}</strong> / {aiUsage.teamLimit.toLocaleString()}건
                          <span className="ml-2 text-gray-500">(본인: {aiUsage.used.toLocaleString()}건)</span>
                        </>
                      ) : (
                        <>
                          본인 사용량: <strong>{aiUsage.used.toLocaleString()}</strong> / {aiUsage.limit.toLocaleString()}건
                        </>
                      )}
                      <span className="ml-2 text-gray-500">({aiUsage.teamName} 팀, {aiUsage.role === 'OWNER' ? '팀장' : '팀원'}, {aiUsage.plan})</span>
                    </>
                  ) : (
                    <>
                      이번 달 AI 사용량: <strong>{aiUsage.used.toLocaleString()}</strong> / {aiUsage.limit.toLocaleString()}건
                      <span className="ml-2 text-gray-500">({aiUsage.plan} 플랜입니다)</span>
                    </>
                  )}
                </span>
                {aiUsage.isTeamContext ? (
                  aiUsage.teamTotalUsed !== null && aiUsage.teamLimit !== null && aiUsage.teamTotalUsed >= aiUsage.teamLimit && (
                    <span className="text-red-600 font-semibold text-xs">⚠️ 팀 한도 초과 &mdash; 관리자에게 문의해 주세요.</span>
                  )
                ) : (
                  !aiUsage.canUse && (
                    <span className="text-red-600 font-semibold text-xs">⚠️ 한도 초과 &mdash; 관리자에게 문의해 주세요.</span>
                  )
                )}
                {!aiUsage.isTeamContext && aiUsage.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (
                  (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0) ? (
                    <span className="text-orange-600 font-semibold text-xs">
                      ⚠️ 오늘 {(aiUsage.dailyUsed ?? 0)}/{aiUsage.dailyLimit}건 &mdash; 일일 한도 초과. 내일 다시 이용하거나 플랜을 업그레이드하세요.
                    </span>
                  ) : (
                    <span className="text-gray-500 text-xs">
                      오늘 {(aiUsage.dailyUsed ?? 0)}/{aiUsage.dailyLimit}건 (무료 플랜 일일 한도)
                    </span>
                  )
                )}
              </div>
            )}

            {/* 사용 가이드 */}
            {showGuide && (
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-900">
                    <Info className="w-5 h-5" />
                    이미지 업로드 사용 가이드
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">📌 기능 개요</h4>
                    <p className="text-gray-700">
                      로컬 PC에 저장된 이미지 파일을 직접 업로드하여 <strong>ALT 텍스트를 자동으로 생성</strong>하는 기능입니다.
                      외부 URL 없이도 사용 가능하며, 한 번에 여러 이미지를 처리할 수 있습니다.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">🎯 사용 방법</h4>
                    <ol className="list-decimal pl-5 space-y-1 text-gray-700">
                      <li><strong>드래그 & 드롭</strong>: 이미지 파일을 점선 영역으로 끌어다 놓기</li>
                      <li><strong>파일 선택</strong>: "파일 선택" 버튼을 클릭하여 파일 탐색기로 선택</li>
                      <li>여러 파일을 동시에 선택 가능 (Ctrl/Cmd + 클릭)</li>
                      <li>"모두 업로드" 버튼으로 업로드 및 ALT 생성 시작</li>
                      <li>각 이미지의 진행률 및 상태 확인</li>
                      <li>생성된 ALT 텍스트 확인 및 복사</li>
                    </ol>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">💾 지원 파일 형식</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <Badge className="bg-blue-600 text-white justify-center">JPEG / JPG</Badge>
                      <Badge className="bg-green-600 text-white justify-center">PNG</Badge>
                      <Badge className="bg-purple-600 text-white justify-center">WEBP</Badge>
                      <Badge className="bg-orange-600 text-white justify-center">GIF</Badge>
                    </div>
                    <p className="text-gray-600 text-xs mt-2">
                      ✓ 고품질 이미진 추천: 100KB ~ 10MB, 최소 200x200px
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">⚡ 대량 업로드 팁</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li><strong>한 번에 최대 20개</strong>까지 업로드 권장 (센버 부하 고려)</li>
                      <li>각 파일의 업로드 진행률을 실시간으로 확인할 수 있습니다</li>
                      <li>오류 파일은 개별적으로 재시도 가능합니다</li>
                      <li>업로드 중 "선택 취소" 버튼으로 전체 취소 가능</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">💡 팁</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li>ALT 텍스트를 더블클릭하면 바로 복사됩니다</li>
                      <li>HTML 태그 버튼으로 전체 img 태그를 복사할 수 있습니다</li>
                      <li>필터 기능으로 대기/완료 상태만 볼 수 있습니다</li>
                      <li>이력 탭에서 이전에 업로드한 파일을 확인할 수 있습니다</li>
                      <li><strong>캐시 시스템</strong>: 동일 이미지는 캐시에서 빠르게 제공됩니다 (캐시 횟수 표시)</li>
                      <li><strong>강제 재분석</strong>: 캐시가 있는 경우 "강제 재분석" 버튼으로 새로 생성 가능 (AI 사용량 차감)</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">🔄 ALT 생성 타입</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li><Badge className="bg-green-600 text-white text-xs">AI</Badge> OpenAI로 생성된 고품질 ALT 텍스트</li>
                      <li><Badge className="bg-yellow-600 text-white text-xs">FALLBACK</Badge> Google Vision 등 대체 API로 생성</li>
                      <li><Badge className="bg-gray-600 text-white text-xs">PENDING</Badge> 아직 생성되지 않음</li>
                      <li><Badge className="bg-red-600 text-white text-xs">ERROR</Badge> 생성 오류</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">⭐ 언제 사용하나요?</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li><strong>로컬 파일</strong>: 웹에 올리기 전 로컬 PC의 이미지 파일</li>
                      <li><strong>사적 이미지</strong>: 로그인이 필요하여 URL로 접근 불가한 이미지</li>
                      <li><strong>스크린샷</strong>: 캐분처나 문서 스크린샷</li>
                      <li><strong>대량 처리</strong>: 여러 이미지를 한 번에 처리</li>
                      <li><strong>CMS 업로드 전</strong>: 커뮤니티/블로그에 업로드하기 전 ALT 준비</li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                    <p className="text-yellow-900 text-xs">
                      <strong>⚠️ 보안:</strong> 업로드된 이미지는 서버에 저장되며, 삭제 하기 전까지 보관됩니다. 
                      민감한 정보가 포함된 이미지는 업로드하지 마세요.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {isSuperAdmin && (
              <Alert className="bg-amber-50 border-amber-300">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  SUPER_ADMIN 계정은 ALT 생성 기능을 사용할 수 없습니다. ALT 생성은 CLIENT 계정으로 로그인하여 이용해 주세요.
                </AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* 컨텍스트 프로필 옵션 */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="useContextProfile"
                checked={useContextProfile}
                onChange={(e) => setUseContextProfile(e.target.checked)}
                disabled={!profileActive}
                className="w-4 h-4 cursor-pointer disabled:cursor-not-allowed"
              />
              <label
                htmlFor="useContextProfile"
                className={`text-sm select-none ${profileActive ? 'cursor-pointer' : 'cursor-not-allowed text-gray-400'}`}
              >
                개인별 컨텍스트 프로필 적용
                {profileActive
                  ? <span className="text-xs text-gray-500 ml-1">(설정 → 컨텍스트 프로필에서 업종/사이트 유형 등 설정 가능)</span>
                  : <span className="text-xs text-amber-600 ml-1">(컨텍스트 프로필을 먼저 등록해주세요 → 프로필 메뉴)</span>
                }
              </label>
            </div>

            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive ? 'border-primary bg-blue-50' : 'border-gray-300'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/tiff,image/avif,image/svg+xml,.jfif,.jpe"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
                multiple
              />

              {selectedFiles.length === 0 ? (
                <div className="space-y-4">
                  <Upload className="w-16 h-16 mx-auto text-gray-400" />
                  <div>
                    <p className="text-lg font-semibold text-gray-700 mb-2">
                      이미지를 드래그하여 놓거나 클릭하여 선택하세요
                    </p>
                    <p className="text-sm text-gray-500">
                      JPG, PNG, GIF, WEBP 형식 지원 ({maxAllowedSizeKb > 0 ? (maxAllowedSizeKb >= 1024 ? `최대 ${(maxAllowedSizeKb / 1024).toFixed(0)}MB` : `최대 ${maxAllowedSizeKb}KB`) : MAX_UPLOAD_SIZE_LABEL}, 최대 10개 파일)
                    </p>
                    {process.env.NODE_ENV === 'production' && (
                      <p className="text-xs text-amber-600 mt-1 font-medium">
                        ⚠ 운영 환경에서는 파일당 {MAX_UPLOAD_SIZE_MB}MB 이하만 업로드 가능합니다.
                      </p>
                    )}
                    {maxAllowedSizeKb > 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠ 서버 설정에 의해 파일당 {maxAllowedSizeKb >= 1024 ? `${(maxAllowedSizeKb / 1024).toFixed(0)}MB` : `${maxAllowedSizeKb}KB`} 이하만 업로드 가능합니다.
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    className="mt-4"
                  >
                    파일 선택
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">
                      선택된 파일 ({selectedFiles.length}/10)
                    </h3>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        variant="outline"
                        size="sm"
                        disabled={selectedFiles.length >= 10 || uploading}
                      >
                        파일 추가
                      </Button>
                      <Button
                        onClick={handleClearSelection}
                        variant="outline"
                        size="sm"
                        disabled={uploading}
                      >
                        전체 제거
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[500px] overflow-y-auto">
                    {selectedFiles.map((fileInfo, index) => (
                      <Card key={index} className="relative">
                        <CardContent className="p-4">
                          <div className="flex gap-3">
                            <img
                              src={fileInfo.previewUrl}
                              alt={fileInfo.file.name}
                              className="w-20 h-20 object-cover rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold truncate">
                                {fileInfo.file.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {(fileInfo.file.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                              
                              {fileInfo.status === 'pending' && (
                                <Badge className="mt-2 bg-gray-200 text-gray-800">
                                  <Clock className="w-3 h-3 mr-1" />
                                  대기중
                                </Badge>
                              )}
                              
                              {fileInfo.status === 'uploading' && (
                                <div className="mt-2 space-y-1">
                                  <Badge className="bg-blue-200 text-blue-800">
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    업로드 중
                                  </Badge>
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                      className="bg-blue-600 h-2 rounded-full transition-all"
                                      style={{ width: `${fileInfo.progress}%` }}
                                    />
                                  </div>
                                  <p className="text-xs text-gray-600">{fileInfo.progress}%</p>
                                </div>
                              )}
                              
                              {fileInfo.status === 'completed' && (
                                <Badge className="mt-2 bg-green-200 text-green-800">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  완료
                                </Badge>
                              )}
                              
                              {fileInfo.status === 'failed' && (
                                <div className="mt-2">
                                  <Badge className="bg-red-200 text-red-800">
                                    <AlertCircle className="w-3 h-3 mr-1" />
                                    오류
                                  </Badge>
                                  <p className="text-xs text-red-600 mt-1">{fileInfo.error}</p>
                                </div>
                              )}
                            </div>
                            
                            {fileInfo.status !== 'uploading' && (
                              <Button
                                onClick={() => handleRemoveFile(index)}
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <div className="flex flex-col gap-3 pt-4 border-t">
                    {uploadSuccess && (
                      <Alert className="bg-green-50 border-green-300">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <AlertDescription className="text-green-800">{uploadSuccess}</AlertDescription>
                      </Alert>
                    )}
                    <div className="flex gap-2 justify-center">
                      <Button
                        onClick={handleUpload}
                        disabled={uploading || selectedFiles.every(f => f.status === 'completed') || isSuperAdmin || !!(aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))}
                        className="w-full md:w-auto"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            업로드 중... ({selectedFiles.filter(f => f.status === 'completed').length}/{selectedFiles.length})
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            {selectedFiles.every(f => f.status === 'completed')
                              ? '업로드 완료'
                              : selectedFiles.some(f => f.status === 'completed')
                              ? '나머지 이미지 업로드'
                              : '전체 업로드 및 ALT 생성'
                            }
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {selectedFiles.some(f => f.status === 'completed' && f.result) && (
              <Card className="bg-green-50 border-green-200">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">업로드 완료</CardTitle>
                      <CardDescription>
                        {selectedFiles.filter(f => f.status === 'completed').length}개 파일이 성공적으로 업로드되었습니다
                      </CardDescription>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedFiles(prev => prev.filter(f => f.status !== 'completed'))}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[400px] overflow-y-auto">
                    {selectedFiles
                      .filter(f => f.status === 'completed' && f.result)
                      .map((fileInfo, index) => {
                        const displayAlt = getDisplayAltText(
                          fileInfo.result!.generatedAlt,
                          fileInfo.result!.altGenerationType,
                          fileInfo.result!.status
                        );
                        const isAiCompleted = fileInfo.result!.altGenerationType === 'AI' || 
                                             fileInfo.result!.altGenerationType === 'FALLBACK';
                        const isPending = fileInfo.result!.status === 'PENDING' || 
                                         fileInfo.result!.altGenerationType === 'PENDING';
                        
                        return (
                          <Card 
                            key={index} 
                            className={`bg-white ${
                              isAiCompleted ? 'border-green-300' : 
                              isPending ? 'border-orange-300' : 
                              'border-gray-200'
                            }`}
                          >
                            <CardContent className="p-4">
                              <div className="flex gap-3">
                                <img
                                  src={fileInfo.previewUrl}
                                  alt={fileInfo.result!.generatedAlt || ''}
                                  className="w-24 h-24 object-cover rounded-lg"
                                />
                                <div className="flex-1 space-y-2">
                                  <div className="flex gap-2 flex-wrap">
                                    {getStatusBadge(fileInfo.result!.status)}
                                    {getAltTypeBadge(fileInfo.result!.altGenerationType)}
                                    {fileInfo.result!.cachecnt !== undefined && (
                                      <Badge className="bg-purple-100 text-purple-700 border border-purple-200 text-xs">
                                        캐시 {fileInfo.result!.cachecnt}회
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-600 truncate">
                                    {fileInfo.file.name}
                                  </p>
                                  {isPending && (
                                    <div className="bg-orange-50 border border-orange-200 rounded p-2 text-xs text-orange-800">
                                      ⏳ AI 분석 중... 잠시 후 새로고침됩니다
                                    </div>
                                  )}
                                  {displayAlt && (
                                    <div className={`p-2 rounded text-xs ${
                                      fileInfo.result!.altGenerationType === 'AI' ? 'bg-green-50 text-green-800 border border-green-200' :
                                      fileInfo.result!.altGenerationType === 'FALLBACK' ? 'bg-yellow-50 text-yellow-800 border border-yellow-200' :
                                      'bg-gray-50 text-gray-800'
                                    }`}>
                                      <p className="font-semibold mb-1">
                                        {isAiCompleted ? '✅ ALT:' : 'ALT:'}
                                      </p>
                                      <p>{displayAlt}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>

        {images.length > 0 && (() => {
          const stats = getAltTypeStats();
          return (
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
              <CardHeader>
                <CardTitle className="text-lg">ALT 텍스트 생성 통계
                  <span className="text-sm font-normal text-gray-500 ml-2">— 현재 페이지 기준</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <p className="text-3xl font-bold text-green-600">{stats.ai}</p>
                    <p className="text-xs text-gray-600 mt-1">AI 분석</p>
                    <p className="text-xs text-green-600 font-semibold">{stats.aiPercentage}% 정확도</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <p className="text-3xl font-bold text-yellow-600">{stats.fallback}</p>
                    <p className="text-xs text-gray-600 mt-1">임시 생성</p>
                    <p className="text-xs text-yellow-600 font-semibold">재분석 권장</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <p className="text-3xl font-bold text-gray-600">{stats.pending}</p>
                    <p className="text-xs text-gray-600 mt-1">미생성</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <p className="text-3xl font-bold text-red-600">{stats.error}</p>
                    <p className="text-xs text-gray-600 mt-1">오류</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <p className="text-3xl font-bold text-purple-600">{stats.skippedDecorative}</p>
                    <p className="text-xs text-gray-600 mt-1">장식용 이미지</p>
                    <p className="text-xs text-purple-600 font-semibold">ALT 생성 건너뜀</p>
                  </div>
                </div>
                {stats.fallback > stats.ai && stats.fallback > 0 && (
                  <Alert className="mt-4 bg-yellow-50 border-yellow-300">
                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-yellow-800">
                      <strong>⚠️ 임시 생성된 ALT 텍스트가 많습니다.</strong>
                      <br />
                      임시 생성된 항목은 파일명 기반으로 만들어져 정확하지 않을 수 있습니다.
                      각 항목의 "AI 재분석" 버튼을 클릭하거나 해당 이미지를 다시 업로드해 주세요.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          );
        })()}

        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <CardTitle className="text-xl">이미지 목록({totalElements.toLocaleString()}건)</CardTitle>
              <div className="flex gap-2">
                {images.length > 0 && (
                  <Button
                    onClick={handleExcelDownload}
                    disabled={excelLoading}
                    variant="outline"
                    size="sm"
                    className="text-green-700 border-green-400 hover:bg-green-50"
                  >
                    {excelLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />다운로드 중...</>
                    ) : (
                      <><FileDown className="w-4 h-4 mr-2" />엑셀 다운로드 ({totalElements.toLocaleString()}건)</>
                    )}
                  </Button>
                )}
                {images.length > 0 && (
                  <Button
                    onClick={handleClearAll}
                    variant="destructive"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    전체 삭제
                  </Button>
                )}
                  <Button
                    onClick={() => { setAltTypeFilter('ALL'); fetchImages(0, filter === 'ALL' ? null : filter, pageSize, undefined, undefined, 'ALL'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'ALL' ? 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600' : ''}
                  >
                    전체
                  </Button>
                  <Button
                    onClick={() => { setAltTypeFilter('AI'); fetchImages(0, filter === 'ALL' ? null : filter, pageSize, undefined, undefined, 'AI'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'AI' ? 'bg-green-600 text-white hover:bg-green-700 border-green-600' : ''}
                  >
                    ✓ AI 분석
                  </Button>
                  <Button
                    onClick={() => { setAltTypeFilter('FALLBACK'); fetchImages(0, filter === 'ALL' ? null : filter, pageSize, undefined, undefined, 'FALLBACK'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'FALLBACK' ? 'bg-yellow-600 text-white hover:bg-yellow-700 border-yellow-600' : ''}
                  >
                    ⚠ 임시 생성
                  </Button>
                  <Button
                    onClick={() => { setAltTypeFilter('PENDING'); fetchImages(0, filter === 'ALL' ? null : filter, pageSize, undefined, undefined, 'PENDING'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'PENDING' ? 'bg-gray-600 text-white hover:bg-gray-700 border-gray-600' : ''}
                  >
                    📝 미생성
                  </Button>
                  <Button
                    onClick={() => { setAltTypeFilter('ERROR'); fetchImages(0, filter === 'ALL' ? null : filter, pageSize, undefined, undefined, 'ERROR'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'ERROR' ? 'bg-red-600 text-white hover:bg-red-700 border-red-600' : ''}
                  >
                    ✗ 오류
                  </Button>
                  <Button
                    onClick={() => { setAltTypeFilter('SKIPPED_DECORATIVE'); fetchImages(0, filter === 'ALL' ? null : filter, pageSize, undefined, undefined, 'SKIPPED_DECORATIVE'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'SKIPPED_DECORATIVE' ? 'bg-purple-600 text-white hover:bg-purple-700 border-purple-600' : ''}
                  >
                    🎨 장식용
                  </Button>
                </div>
              </div>
          </CardHeader>
          <CardContent>
            <div>

              <Tabs value={filter} onValueChange={(v) => {
                const newFilter = v as 'ALL' | 'PENDING' | 'COMPLETED';
                setFilter(newFilter);
                fetchImages(0, newFilter === 'ALL' ? null : newFilter, pageSize, undefined, undefined, undefined, startDate, endDate);
              }} className="w-full">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                  <TabsList className="grid grid-cols-3 flex-1">
                    <TabsTrigger value="ALL">전체</TabsTrigger>
                    <TabsTrigger value="PENDING">대기</TabsTrigger>
                    <TabsTrigger value="COMPLETED">완료</TabsTrigger>
                  </TabsList>
                  <div className="flex items-center gap-1 shrink-0">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => { setStartDate(e.target.value); fetchImages(0, filter === 'ALL' ? null : filter, pageSize, undefined, undefined, undefined, e.target.value, endDate); }}
                        className="h-9 w-full md:w-auto text-xs"
                      />
                      <span className="text-gray-500 text-xs">~</span>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => { setEndDate(e.target.value); fetchImages(0, filter === 'ALL' ? null : filter, pageSize, undefined, undefined, undefined, startDate, e.target.value); }}
                        className="h-9 w-full md:w-auto text-xs"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Select
                      value={`${sortBy}_${sortDir}`}
                      onValueChange={(v) => {
                        const lastIdx = v.lastIndexOf('_');
                        const newSortBy = v.substring(0, lastIdx) as SortBy;
                        const newSortDir = v.substring(lastIdx + 1) as SortDir;
                        setSortBy(newSortBy);
                        setSortDir(newSortDir);
                        fetchImages(0, filter === 'ALL' ? null : filter, pageSize, newSortBy, newSortDir, undefined, startDate, endDate);
                      }}
                    >
                      <SelectTrigger className="w-[180px] h-9 text-sm">
                        <SelectValue placeholder="정렬" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UPDATED_AT_DESC">수정일 최신순{sortIsDefault && sortBy === 'UPDATED_AT' && sortDir === 'DESC' ? ' (기본)' : ''}</SelectItem>
                        <SelectItem value="UPDATED_AT_ASC">수정일 오래된순</SelectItem>
                        <SelectItem value="CREATED_AT_DESC">등록일 최신순</SelectItem>
                        <SelectItem value="CREATED_AT_ASC">등록일 오래된순</SelectItem>
                        <SelectItem value="STATUS_ASC">상태순</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <TabsContent value={filter} className="mt-6">
                  {loading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <Loader2 className="w-12 h-12 animate-spin text-blue-500 mb-4" />
                      <p className="text-gray-500">데이터를 불러오는 중...</p>
                    </div>
                  ) : filteredImages.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Upload className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>{images.length === 0 ? '이전에 업로드한 이미지 목록이 없습니다' : '해당 조건의 이미지가 없습니다'}</p>
                      {images.length === 0 && (
                        <p className="text-sm mt-2">이미지를 업로드하여 ALT 텍스트를 생성해보세요</p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredImages.map((img) => {
                        const displayAlt = getDisplayAltText(img.generatedAlt, img.altGenerationType, img.status);
                        return (
                        <Card key={img.id} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex flex-col md:flex-row gap-4">
                              <AuthenticatedImage
                                src={img.displayImageUrl ?? (img.imageSrc?.startsWith('http') ? img.imageSrc : img.imageSrc?.startsWith('/api/') ? `${BASE}${img.imageSrc}` : `${UPLOADS_BASE_URL}/${img.imageSrc?.replace(/^\/+/, '') ?? ''}`)}
                                alt={img.generatedAlt || ''}
                                className="w-full md:w-32 h-32 object-cover rounded-lg"
                              />
                              <div className="flex-1 space-y-2">
                                <div className="flex gap-2 flex-wrap">
                                  {getStatusBadge(img.status)}
                                  {getAltTypeBadge(img.altGenerationType)}
                                  {img.cachecnt !== undefined && (
                                    <Badge className="bg-purple-100 text-purple-700 border border-purple-200 text-xs">
                                      캐시 {img.cachecnt}회
                                    </Badge>
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm text-gray-600">
                                    <span className="font-semibold">원본 파일:</span> {img.url?.split(/[\\/]/).pop() || img.url}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {img.updatedAt ? '최종 수정' : '등록'}: {formatRelativeTime(img.updatedAt || img.createdAt)} ({formatDateTime(img.updatedAt || img.createdAt)})
                                  </p>
                                </div>
                                {displayAlt ? (
                                  <Card className={`${
                                    img.altGenerationType === 'AI' ? 'bg-green-50 border-green-200' :
                                    img.altGenerationType === 'FALLBACK' ? 'bg-yellow-50 border-yellow-200' :
                                    'bg-gray-50 border-gray-200'
                                  }`}>
                                    <CardContent className="p-3">
                                      <div className="flex flex-col sm:flex-row gap-2">
                                        <div className="flex-1 min-w-0">
                                          <p 
                                            className={`text-sm cursor-pointer hover:bg-white/50 p-2 rounded transition-colors ${
                                              img.altGenerationType === 'AI' && img.status === 'COMPLETED' ? 'font-semibold' : ''
                                            } ${
                                              img.altGenerationType === 'AI' ? 'text-green-800' :
                                              img.altGenerationType === 'FALLBACK' ? 'text-yellow-800' :
                                              img.altGenerationType === 'ERROR' ? 'text-red-600' :
                                              'text-gray-800'
                                            } ${
                                              img.altGenerationType !== 'ERROR' ? 'cursor-pointer hover:bg-white/50' : ''
                                            }`}
                                            {...(img.altGenerationType !== 'ERROR' && {
                                              onDoubleClick: () => handleDoubleClick(img.generatedAlt!),
                                              title: '더블클릭하여 복사'
                                            })}
                                          >
                                            {displayAlt}
                                          </p>
                                          {img.altGenerationType === 'FALLBACK' && (
                                            <p className="text-xs text-yellow-700 mt-2 italic">
                                              💡 이 설명은 임시 생성되었습니다. 정확하지 않을 수 있습니다.
                                            </p>
                                          )}
                                        </div>
                                        {/* 커스텀 ALT 섹션 */}
                                        <div className="mt-4">
                                          <p className="text-xs font-semibold text-gray-500 mb-2">✏️ 커스텀 ALT</p>
                                          <CustomAltEditor
                                            imageId={img.id}
                                            customAltText={img.customAltText}
                                            onSaved={(customAltText) => handleCustomAltSaved(img.id, customAltText)}
                                            onDeleted={() => handleCustomAltDeleted(img.id)}
                                          />
                                        </div>
                                        <div className="flex flex-wrap gap-2 shrink-0">
                                          {(img.altGenerationType !== 'FALLBACK' && img.altGenerationType !== 'ERROR') && (
                                            <div className="flex flex-wrap gap-2">
                                              <Button
                                                onClick={() => handleCopyAlt(img.generatedAlt!, img.id)}
                                                size="sm"
                                                variant="outline"
                                                className="whitespace-nowrap"
                                              >
                                                {copiedId === img.id ? (
                                                  <>
                                                    <Check className="w-3 h-3 mr-1 text-green-600" />
                                                    복사됨
                                                  </>
                                                ) : (
                                                  <>
                                                    <Copy className="w-3 h-3 mr-1" />
                                                    ALT 복사
                                                  </>
                                                )}
                                              </Button>
                                              <Button
                                                onClick={() => handleCopyHtml(img)}
                                                size="sm"
                                                variant="outline"
                                                className="whitespace-nowrap"
                                              >
                                                {copiedHtmlId === img.id ? (
                                                  <>
                                                    <Check className="w-3 h-3 mr-1 text-green-600" />
                                                    복사됨
                                                  </>
                                                ) : (
                                                  <>
                                                    <Copy className="w-3 h-3 mr-1" />
                                                    HTML 복사
                                                  </>
                                                )}
                                              </Button>
                                            </div>
                                          )}
                                          <div className="flex flex-wrap gap-2">
                                            {img.status !== 'COMPLETED' && (!img.cachecnt || img.cachecnt === 0) && (
                                              <Button
                                                onClick={() => handleRegenerateAlt(img.id)}
                                                variant="outline"
                                                size="sm"
                                                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                                disabled={regeneratingIds.has(img.id) || !!(aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))}
                                              >
                                                {regeneratingIds.has(img.id) ? (
                                                  <>
                                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                                    분석중...
                                                  </>
                                                ) : (
                                                  <>🔄 재분석</>
                                                )}
                                              </Button>
                                            )}
                                            <Button
                                              onClick={() => handleDeleteImage(img.id)}
                                              variant="destructive"
                                              size="sm"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                ) : (
                                  <Card className={isLongPending(img.createdAt) ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}>
                                    <CardContent className="p-3">
                                      <div className="flex flex-col sm:flex-row gap-2">
                                        <div className="flex-1 min-w-0">
                                          {isLongPending(img.createdAt) ? (
                                            <>
                                              <p className="text-sm text-amber-800 font-medium">
                                                ⚠️ AI 분석이 자동으로 완료되지 않았습니다.
                                              </p>
                                              <p className="text-xs text-amber-700 mt-1">
                                                아래 버튼을 눌러 직접 ALT를 생성해 주세요.
                                              </p>
                                            </>
                                          ) : (
                                            <>
                                              <p className="text-sm text-blue-800 font-medium">
                                                ⏳ AI가 ALT 텍스트를 분석 중입니다.
                                              </p>
                                              <p className="text-xs text-blue-600 mt-1">
                                                잠시 후 자동으로 결과가 표시됩니다. (약 10초 소요)
                                              </p>
                                            </>
                                          )}
                                        </div>
                                        <div className="flex flex-wrap gap-2 shrink-0">
                                          <Button
                                            onClick={() => handleRegenerateAlt(img.id)}
                                            variant="outline"
                                            size="sm"
                                            className="text-blue-600 border-blue-300 hover:bg-blue-100 whitespace-nowrap"
                                            disabled={regeneratingIds.has(img.id) || !!(aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))}
                                          >
                                            {regeneratingIds.has(img.id) ? (
                                              <>
                                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                                분석중...
                                              </>
                                            ) : (
                                              <>🔄 ALT 생성</>
                                            )}
                                          </Button>
                                          {img.cachecnt !== undefined && img.cachecnt > 0 && (
                                            <Button
                                              onClick={() => handleRegenerateAlt(img.id, true)}
                                              variant="outline"
                                              size="sm"
                                              className="text-purple-600 border-purple-300 hover:bg-purple-100 whitespace-nowrap"
                                              disabled={regeneratingIds.has(img.id) || !!(aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))}
                                            >
                                              {regeneratingIds.has(img.id) ? (
                                                <>
                                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                                  강제 재분석 중...
                                                </>
                                              ) : (
                                                <>🔄 강제 재분석</>
                                              )}
                                            </Button>
                                          )}
                                          <Button
                                            onClick={() => handleDeleteImage(img.id)}
                                            variant="destructive"
                                            size="sm"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        );
                      })}

                      {images.length > 0 && (
                        <div className="flex justify-between items-center mt-6">
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <span>페이지당</span>
                            <Select
                              value={String(pageSize)}
                              onValueChange={(v) => {
                                const newSize = Number(v);
                                setPageSize(newSize);
                                setCurrentPage(0);
                                fetchImages(0, filter === 'ALL' ? null : filter, newSize);
                              }}
                            >
                              <SelectTrigger className="w-20 h-8 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="10">10개</SelectItem>
                                <SelectItem value="20">20개</SelectItem>
                                <SelectItem value="50">50개</SelectItem>
                                <SelectItem value="100">100개</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {totalPages > 1 && (
                            <div className="flex items-center gap-2">
                              <Button
                                onClick={() => fetchImages(currentPage - 1, filter === 'ALL' ? null : filter)}
                                disabled={currentPage === 0}
                                variant="outline"
                              >
                                이전
                              </Button>
                              <span className="px-4 py-2 text-sm">
                                {currentPage + 1} / {totalPages}
                              </span>
                              <Button
                                onClick={() => fetchImages(currentPage + 1, filter === 'ALL' ? null : filter)}
                                disabled={currentPage >= totalPages - 1}
                                variant="outline"
                              >
                                다음
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}

export default AlttextImgUpload;
